import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Analytics, logEvent, setUserId } from '@angular/fire/analytics';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  constructor(
    @Optional() private analytics: Analytics | null,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  logPageView(pagePath: string) {
    if (!this.analytics) return;

    logEvent(this.analytics, 'page_view', {
      page_path: pagePath,
      page_location: this.document.location.href,
      page_title: this.document.title,
    });
  }

  logEvent(eventName: string, params?: Record<string, unknown>) {
    if (!this.analytics) return;

    logEvent(this.analytics, eventName, params);
  }

  /** Track any custom event */
  trackEvent(eventName: string, eventParams?: Record<string, unknown>) {
    if (!this.analytics) return;

    logEvent(this.analytics, eventName, eventParams);
  }

  /** Track button clicks */
  trackClick(buttonName: string) {
    this.trackEvent('button_click', { buttonName });
  }

  /** Optional: Set logged-in user ID for analytics */
  setUserId(userId: string) {
    if (!this.analytics) return;

    setUserId(this.analytics, userId);
  }

  /** Clear user on logout */
  clearUser(): void {
    if (!this.analytics) return;

    setUserId(this.analytics, null);

    this.trackEvent('logout', {
      screen_name:
        this.document?.title ||
        this.document?.location?.pathname?.replace('/', '') ||
        'unknown_screen',
    });
  }

  /** Centralized API error tracking */
  trackApiError(request: HttpRequest<unknown>, error: HttpErrorResponse): void {
    const screenName =
      this.document?.title ||
      this.document?.location?.pathname?.replace('/', '') ||
      'unknown_screen';

    this.trackEvent('api_error', {
      endpoint: this.sanitizeUrl(request.url),
      method: request.method,
      status_code: error.status || 0,
      error_type: this.resolveErrorType(error),
      screen_name: screenName,
      message: this.resolveErrorMessage(error),
    });
  }

  private sanitizeUrl(url: string): string {
    return url.split('?')[0].replace(/\/\d+/g, '/:id');
  }

  private resolveErrorType(error: HttpErrorResponse): string {
    if (error.status === 0) return 'network_error';
    if (error.status === 401) return 'unauthorized';
    if (error.status === 403) return 'forbidden';
    if (error.status === 451) return 'terms_not_accepted';
    if (error.status === 404) return 'not_found';
    if (error.status >= 500) return 'server_error';
    return 'client_error';
  }

  private resolveErrorMessage(error: HttpErrorResponse): string {
    return (
      error.error?.message ||
      error.error?.error ||
      error.message ||
      'unknown_error'
    );
  }
}
